import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { IconeBusca, IconeSeta } from '../Icones.js';

/**
 * Barra de filtros de uma lista: busca, campos, quantos filtros estao ativos,
 * "Limpar" e o contador do recorte. O filtro e o jeito principal de navegar
 * numa lista longa, entao no desktop ela gruda no alto da tela.
 *
 * No celular ela rola junto com a pagina (barra fixa ali come a tela) e os
 * campos comecam recolhidos atras de "Filtros (N)".
 */
export function BarraFiltros({
  abas,
  busca,
  topo,
  campos,
  ativos,
  contagem,
  onLimpar,
  extra,
}: {
  /** Primeira linha da barra (ex.: classes do plantel): presa no alto junto com os filtros. */
  abas?: ReactNode;
  busca?: { valor: string; onMudar: (v: string) => void; rotulo: string; placeholder: string };
  /** A esquerda da busca, ex.: o periodo (Alternador). */
  topo?: ReactNode;
  /** CampoFiltro em grade. */
  campos?: ReactNode;
  /** Quantos filtros estao valendo (busca inclusa). */
  ativos: number;
  /** Ex.: "26 animais". Anunciado ao leitor de tela quando muda. */
  contagem: ReactNode;
  onLimpar?: () => void;
  /** A direita da ultima linha, ex.: Lista | Cartoes. */
  extra?: ReactNode;
}) {
  const [abertos, setAbertos] = useState(false);
  const idCampos = useId();
  const campoBusca = useRef<HTMLInputElement>(null);

  // "/" leva direto para a busca, como nos sistemas de consulta.
  const temBusca = !!busca;
  useEffect(() => {
    if (!temBusca) return;
    const tecla = (e: KeyboardEvent) => {
      const alvo = e.target as HTMLElement;
      if (e.key !== '/' || alvo.closest('input, textarea, select, [contenteditable]')) return;
      e.preventDefault();
      campoBusca.current?.focus();
    };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [temBusca]);

  return (
    <section
      aria-label="Filtros"
      className="-mx-4 space-y-3 border-y border-borda bg-fundo/92 px-4 py-3 backdrop-blur-md lg:sticky lg:top-0 lg:z-10 lg:-mx-8 lg:border-t-0 lg:px-8"
    >
      {abas && <div className="-mt-1">{abas}</div>}
      {(topo || busca) && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          {topo}
          {busca && (
            <label className="relative min-w-0 flex-1 basis-60 sm:max-w-sm">
              <span className="sr-only">{busca.rotulo}</span>
              <IconeBusca
                width={16}
                height={16}
                className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-apagado"
              />
              <input
                ref={campoBusca}
                type="search"
                value={busca.valor}
                onChange={(e) => busca.onMudar(e.target.value)}
                placeholder={busca.placeholder}
                className="w-full rounded-(--radius-controle) border border-borda bg-superficie py-2 pr-9 pl-9 text-sm placeholder:text-apagado/80 hover:border-borda-forte"
              />
              <kbd
                aria-hidden
                className="pointer-events-none absolute top-1/2 right-2.5 hidden -translate-y-1/2 rounded-[5px] border border-borda px-1.5 font-sans text-[11px] text-apagado md:block"
              >
                /
              </kbd>
            </label>
          )}
        </div>
      )}

      {campos && (
        <div
          id={idCampos}
          className={`grid grid-cols-2 gap-2.5 md:grid-cols-3 xl:grid-cols-6 ${abertos ? '' : 'max-md:hidden'}`}
        >
          {campos}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {campos && (
          <button
            type="button"
            aria-expanded={abertos}
            aria-controls={idCampos}
            onClick={() => setAbertos((a) => !a)}
            className="inline-flex min-h-9 items-center gap-1.5 rounded-(--radius-chip) pointer-coarse:min-h-11 border border-borda bg-superficie px-3.5 text-[13px] font-semibold md:hidden"
          >
            Filtros{ativos > 0 && <span className="numero text-destaque-texto">({ativos})</span>}
            <IconeSeta direcao={abertos ? 'cima' : 'baixo'} width={14} height={14} />
          </button>
        )}
        {onLimpar && ativos > 0 && (
          <button
            type="button"
            onClick={onLimpar}
            className="inline-flex min-h-9 items-center gap-1.5 rounded-(--radius-chip) pointer-coarse:min-h-11 border border-borda bg-superficie px-3.5 text-[13px] font-semibold hover:border-borda-forte"
          >
            <span aria-hidden>×</span> Limpar filtros
          </button>
        )}
        <p aria-live="polite" className="numero text-[13px] text-apagado [&_b]:font-semibold [&_b]:text-texto">
          {contagem}
        </p>
        {extra && <div className="ml-auto">{extra}</div>}
      </div>
    </section>
  );
}

/** Um campo da barra de filtros: rotulo pequeno em cima, select embaixo. Ativo ganha borda Aco. */
export function CampoFiltro<V extends string>({
  rotulo,
  valor,
  onMudar,
  opcoes,
  todos,
}: {
  rotulo: string;
  valor: V | '';
  onMudar: (v: V | '') => void;
  opcoes: readonly { valor: V; rotulo: string }[];
  /** Rotulo da opcao vazia, ex.: "Todas as classes". */
  todos: string;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1">
      <span className="text-[10.5px] font-semibold tracking-[0.08em] text-apagado uppercase">{rotulo}</span>
      <span className="relative">
        <select
          value={valor}
          onChange={(e) => onMudar(e.target.value as V | '')}
          className={`w-full appearance-none truncate rounded-(--radius-controle) border bg-superficie py-2 pr-8 pl-3 text-[13px] font-medium hover:border-borda-forte ${
            valor ? 'border-destaque text-texto' : 'border-borda text-texto'
          }`}
        >
          <option value="">{todos}</option>
          {opcoes.map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.rotulo}
            </option>
          ))}
        </select>
        <IconeSeta
          width={14}
          height={14}
          className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-apagado"
        />
      </span>
    </label>
  );
}

/**
 * O que os numeros da tela significam com os filtros de agora, em linguagem
 * simples ("So potros · avaliacao vencida · ..."). Some quando nao ha filtro.
 */
export function FaixaContextoFiltro({ partes }: { partes: readonly ReactNode[] }) {
  if (!partes.length) return null;
  return (
    <p className="rounded-(--radius-controle) border border-l-[3px] border-borda border-l-destaque bg-superficie px-4 py-2.5 text-[13px] leading-relaxed text-apagado [&_b]:font-semibold [&_b]:text-texto">
      {partes.map((p, i) => (
        <span key={i}>
          {i > 0 && (
            <span aria-hidden className="mx-1.5 opacity-50">
              ·
            </span>
          )}
          {p}
        </span>
      ))}
    </p>
  );
}

export interface Aviso {
  tom: 'erro' | 'aviso' | 'info';
  conteudo: ReactNode;
}

const PONTO: Record<Aviso['tom'], string> = { erro: 'bg-erro', aviso: 'bg-aviso', info: 'bg-destaque' };

/**
 * Avisos recolhidos num `<details>`: nao brigam com o conteudo, mas nao somem.
 * Abre sozinho se ha erro. Sem aviso, vira uma linha quieta ("Nenhum aviso").
 */
export function PainelAvisos({ avisos }: { avisos: readonly Aviso[] }) {
  const grave = avisos.some((a) => a.tom === 'erro');
  const n = avisos.length;
  return (
    <details
      open={grave}
      className={`group rounded-(--radius-controle) border text-[13px] ${
        n ? (grave ? 'border-erro/40 bg-erro/5' : 'border-aviso/35 bg-aviso/5') : 'border-borda bg-superficie'
      }`}
    >
      <summary
        className={`flex min-h-11 cursor-pointer list-none items-center gap-2 px-4 font-semibold select-none [&::-webkit-details-marker]:hidden ${
          n ? (grave ? 'text-erro' : 'text-aviso') : 'font-medium text-apagado'
        }`}
      >
        <IconeSeta width={14} height={14} className="-rotate-90 transition-transform group-open:rotate-0" />
        {n ? `${n} ${n === 1 ? 'aviso' : 'avisos'}` : 'Nenhum aviso'}
      </summary>
      {n > 0 && (
        <ul className="space-y-1.5 px-4 pb-3 pl-10 leading-relaxed text-apagado [&_b]:font-semibold [&_b]:text-texto">
          {avisos.map((a, i) => (
            <li key={i} className="relative">
              <span aria-hidden className={`absolute top-[0.6em] -left-4 size-1.5 rounded-full ${PONTO[a.tom]}`} />
              {a.conteudo}
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}
